import { RootState } from "../../../redux-store";
import { createSelector } from "@reduxjs/toolkit";
import { selectCargoView } from "../../data/cargo-views/selectors";

const selectCargoDialogData = (state: RootState) => state.app.cargoDialog;

export const selectInitialFormData = createSelector(
  selectCargoDialogData,
  (data) => data.initialFormData
);

export const selectResetFormData = createSelector(
  selectCargoDialogData,
  (data) => data.resetFormData
);

const selectPreviousFormData = (state: RootState) =>
  state.app.cargoDialog.previousFormData;

export const selectFormDataForWhenOpeningModal = createSelector(
  selectInitialFormData,
  selectPreviousFormData,
  (initial, previous) => previous || initial
);

export const selectInitialFormDataFromCache = createSelector(
  selectCargoDialogData,
  (data) => data.initialFormDataFromCache
);

export const selectIsSaving = createSelector(
  selectCargoDialogData,
  (data) => data.isSaving
);

export const selectCargoSharedToStatus = createSelector(
  selectCargoDialogData,
  (data) => data.sharedToStatus
);

// The existing cargo the dialog is editing, if any.
export const selectOpenCargoId = (state: RootState): string | null => {
  const { status, editingCargoId } = state.app.cargoDialog;
  return status === "EDIT" ? editingCargoId : null;
};

// Deleted by someone else: either the socket's cargo-view.deleted flagged its
// view, or the dialog found out itself on save / after a reconnect (DORA-458).
export const selectIsCargoDeleted = (cargoId: string) => (state: RootState) =>
  state.app.cargoDialog.deletedCargo?.cargoId === cargoId ||
  selectCargoView(cargoId)(state)?.deleted === true;

// Who deleted a cargo: a dispatcher's user id or a driver's id. Both are null
// when it isn't known (e.g. an older server didn't send it).
export type CargoDeletedBy = {
  deletedBy: string | null;
  deletedByDriver: string | null;
};

// The dispatcher (user id) or driver who deleted a cargo, from what the dialog
// found out itself or else from the socket's deleted view.
const selectCargoDeletedByField =
  (cargoId: string, field: keyof CargoDeletedBy) =>
  (state: RootState): string | null => {
    const { deletedCargo } = state.app.cargoDialog;
    if (deletedCargo?.cargoId === cargoId) {
      return deletedCargo[field];
    }
    return selectCargoView(cargoId)(state)?.[field] ?? null;
  };

export const selectCargoDeletedByDispatcher = (cargoId: string) =>
  selectCargoDeletedByField(cargoId, "deletedBy");

export const selectCargoDeletedByDriver = (cargoId: string) =>
  selectCargoDeletedByField(cargoId, "deletedByDriver");

// The revision the open cargo dialog saves against (DORA-441).
export const selectLoadedRevision = (state: RootState) =>
  state.app.cargoDialog.loadedDataRevisionNumber;

// Someone else's change clashed with the user's unsaved edits (DORA-441).
export const selectRemoteConflict = (state: RootState) =>
  state.app.cargoDialog.remoteConflict;
